/**
 * Frontend Logger Service
 *
 * Centralized logging for frontend with different strategies for dev/prod.
 * In production, errors can be sent to a logging service (e.g., Sentry, LogRocket).
 *
 * This replaces direct console.log/error calls for better observability.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  stack?: string;
}

class Logger {
  private readonly isDevelopment: boolean;
  private readonly minLevel: LogLevel;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.minLevel = this.isDevelopment ? 'debug' : 'warn';
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    return levels.indexOf(level) >= levels.indexOf(this.minLevel);
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
    error?: Error,
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...(context && { context }),
      ...(error?.stack && { stack: error.stack }),
    };
  }

  private output(entry: LogEntry): void {
    if (!this.shouldLog(entry.level)) {
      return;
    }

    if (this.isDevelopment) {
      // Development: Use console with colors
    } else {
      // Production: Send to logging service
      this.sendToLoggingService(entry);
    }
  }

  private sendToLoggingService(entry: LogEntry): void {
    // TODO: Implement integration with logging service (Sentry, LogRocket, etc.)
    // For now, we'll just use console in production with minimal output
    if (entry.level === 'error') {
      // Only log errors in production console
    }
  }

  /**
   * Debug level logging - only in development
   */
  debug(message: string, context?: Record<string, unknown>): void {
    this.output(this.createLogEntry('debug', message, context));
  }

  /**
   * Info level logging - general information
   */
  info(message: string, context?: Record<string, unknown>): void {
    this.output(this.createLogEntry('info', message, context));
  }

  /**
   * Warning level logging - non-critical issues
   */
  warn(message: string, context?: Record<string, unknown>): void {
    this.output(this.createLogEntry('warn', message, context));
  }

  /**
   * Error level logging - critical errors
   */
  error(
    message: string,
    error?: Error,
    context?: Record<string, unknown>,
  ): void {
    this.output(this.createLogEntry('error', message, context, error));
  }

  /**
   * Log API errors with request details
   */
  apiError(
    message: string,
    error: unknown,
    context?: { method?: string; url?: string; status?: number },
  ): void {
    // Type guard for axios-like error
    const isAxiosError = (
      err: unknown,
    ): err is {
      response?: { status?: number; data?: { message?: string } };
      message?: string;
    } => {
      return typeof err === 'object' && err !== null;
    };

    const errorMessage = isAxiosError(error)
      ? error?.response?.data?.message || error?.message || 'Unknown error'
      : 'Unknown error';

    const errorObj = error instanceof Error ? error : new Error(String(error));

    this.error(`${message}: ${errorMessage}`, errorObj, {
      ...context,
      status: isAxiosError(error) ? error?.response?.status : undefined,
      data: isAxiosError(error) ? error?.response?.data : undefined,
    });
  }

  /**
   * Log component lifecycle events (useful for debugging)
   */
  lifecycle(component: string, event: string, data?: unknown): void {
    this.debug(`[${component}] ${event}`, data ? { data } : undefined);
  }

  /**
   * Log performance metrics
   */
  performance(label: string, durationMs: number): void {
    this.info(`Performance: ${label}`, { durationMs });
  }
}

// Export singleton instance
export const logger = new Logger();

/**
 * Performance measurement utility
 *
 * @example
 * const measure = measurePerformance('API Call');
 * await fetchData();
 * measure.end(); // Logs duration automatically
 */
export function measurePerformance(label: string) {
  const start = performance.now();
  return {
    end: () => {
      const duration = performance.now() - start;
      logger.performance(label, duration);
    },
  };
}
